import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { AgenteClaveDTO, AseguradoraClaveResponse, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    activeRamos,
    AGENT_KEYS_BACK_PATH,
    AGENT_KEYS_PATH,
    agentKeyPath,
    errorMessage,
    NEW_AGENT_KEY_PATH,
    RAMOS,
} from '../common/agent-key-common'
import type { AgentKeysLocationState, FormMessage, RamoConfig } from '../common/agent-key-common'

const LOAD_ERROR = 'No se pudo cargar el listado de claves de agente.'
const DELETE_ERROR = 'No se pudo eliminar la clave de agente.'

export interface AgentKeyRow {
    id: number
    insurer: string
    insurerShort: string
    clave: string
    ramos: RamoConfig[]
    sync: boolean
    portalUser: string
    comisiones: Array<{ id: string; short: string; label: string; value: number }>
}

const toRow = (clave: AgenteClaveDTO): AgentKeyRow => ({
    id: Number(clave.id),
    insurer: clave.aseguradora?.aseguradora || clave.aseguradora?.abreviacion || 'Aseguradora',
    insurerShort: clave.aseguradora?.abreviacion || clave.aseguradora?.aseguradora || '—',
    clave: String(clave.clave || '').trim(),
    ramos: activeRamos(clave),
    sync: Boolean(clave.sincronizacionDalala),
    portalUser: String(clave.portalClave || '').trim(),
    comisiones: RAMOS.map((ramo) => ({ id: ramo.id, short: ramo.short, label: ramo.label, value: Number(clave[ramo.comision] || 0) })),
})

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const useAgentKeyListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const locationState = location.state as AgentKeysLocationState | null

    const [search, setSearch] = useState('')
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [message, setMessage] = useState<FormMessage | null>(locationState?.flash ? { type: 'success', message: locationState.flash } : null)
    const [items, setItems] = useState<AgenteClaveDTO[]>([])
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (locationState?.flash) history.replace(AGENT_KEYS_PATH)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response: WrapperResponse<AseguradoraClaveResponse[]> | null = await (await APIInvoke.invokeGET('/aseguradoras/claves')).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                setItems((response.body || []).flatMap((item) => (item.claves || []).map((clave) => ({ ...clave, aseguradora: clave.aseguradora || item.aseguradora }))))
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken])

    const rows = useMemo(() => {
        const query = normalize(search.trim())
        const all = items.map(toRow).sort((left, right) => left.insurer.localeCompare(right.insurer, 'es') || left.clave.localeCompare(right.clave))
        if (!query) return all
        return all.filter((row) => normalize(`${row.insurer} ${row.insurerShort} ${row.clave}`).includes(query))
    }, [items, search])

    const confirmDelete = async () => {
        if (deleteId == null || deleting) return
        try {
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/aseguradoras/accounts/${deleteId}`)).json()
            if (response.ok) {
                setItems((current) => current.filter((item) => item.id !== deleteId))
                setMessage({ type: 'success', message: response.message || 'Clave eliminada correctamente' })
            } else {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
            }
        } catch (deleteError) {
            console.error(deleteError)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
            setDeleteId(null)
        }
    }

    const keyToDelete = rows.find((row) => row.id === deleteId)

    return {
        search,
        loading,
        initialLoadDone,
        error,
        message,
        rows,
        totalItems: items.length,
        syncCount: items.filter((item) => item.sincronizacionDalala).length,
        backTo: AGENT_KEYS_BACK_PATH,
        newLocation: { pathname: NEW_AGENT_KEY_PATH, state: { backUrl: AGENT_KEYS_PATH } },
        deleteOpen: deleteId != null,
        deleteLabel: keyToDelete ? `${keyToDelete.insurerShort} · ${keyToDelete.clave}` : 'esta clave',
        deleting,
        changeSearch: setSearch,
        clearSearch: () => setSearch(''),
        reload: () => setReloadToken((token) => token + 1),
        editKey: (id: number) => history.push({ pathname: agentKeyPath(id), state: { backUrl: AGENT_KEYS_PATH } }),
        askDelete: (id: number) => setDeleteId(id),
        cancelDelete: () => setDeleteId(null),
        confirmDelete,
        dismissMessage: () => setMessage(null),
    }
}
